import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const getSessionId = () => {
  let sid = localStorage.getItem("support_session_id");
  if (!sid) {
    sid = `cs_${crypto.randomUUID()}`;
    localStorage.setItem("support_session_id", sid);
  }
  return sid;
};

const GREETING = {
  role: "assistant",
  text: "Hi! I'm the Eternal Biolabs assistant. Ask me about payments, shipping, rewards or lab results — or leave a message and the team will follow up by email.",
};

const STARTERS = [
  "Where's my order?",
  "How much is shipping?",
  "How do rewards points work?",
  "How do I pay by e-Transfer?",
];

export const ChatWidget = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [email, setEmail] = useState("");
  const [emailSaved, setEmailSaved] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const scrollRef = useRef(null);
  const sendingRef = useRef(false);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    if (!open || loaded) return;
    setLoaded(true);
    api.get(`/support/chat/${getSessionId()}`)
      .then(({ data }) => {
        if (data.messages?.length) setMessages([GREETING, ...data.messages]);
        if (data.email) setEmailSaved(true);
      })
      .catch(() => {});
  }, [open, loaded]);

  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => {
      if (sendingRef.current) return;
      api.get(`/support/chat/${getSessionId()}`)
        .then(({ data }) => {
          if (!data.messages) return;
          setMessages((prev) => (data.messages.length > prev.length - 1 ? [GREETING, ...data.messages] : prev));
        })
        .catch(() => {});
    }, 7000);
    return () => clearInterval(t);
  }, [open]);

  const send = useCallback(async (overrideText) => {
    const text = (typeof overrideText === "string" ? overrideText : input).trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    sendingRef.current = true;
    setMessages((prev) => [...prev, { role: "user", text }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API_BASE}/api/support/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: getSessionId(),
          message: text,
          email: !emailSaved && email.trim() ? email.trim() : undefined,
        }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");
      if (email.trim()) setEmailSaved(true);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let reply = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        reply += decoder.decode(value, { stream: true });
        const snapshot = reply;
        setMessages((prev) => {
          const next = [...prev];
          next[next.length - 1] = { role: "assistant", text: snapshot };
          return next;
        });
      }
      if (!reply) throw new Error("empty reply");
    } catch {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: "assistant",
          text: "Sorry — I couldn't respond just now. Please try again in a moment, or leave your email and message and the team will follow up.",
        };
        return next;
      });
    } finally {
      setSending(false);
      sendingRef.current = false;
    }
  }, [input, sending, email, emailSaved]);

  if (location.pathname.startsWith("/admin")) return null;

  return (
    <>
      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[370px] max-h-[70vh] flex flex-col rounded-2xl overflow-hidden border border-[#DDD7CF] bg-white shadow-2xl"
        >
          <div className="bg-[#8C7B6B] px-5 py-4">
            <p className="text-white font-display text-sm uppercase tracking-[0.15em]">Eternal Biolabs Support</p>
            <p className="text-white/75 text-xs mt-0.5">AI assistant · the team reads every conversation</p>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#F7F5F2] min-h-[260px]" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={`${i}-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-message-${m.role}`}
                  className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-line rounded-2xl ${
                    m.role === "user"
                      ? "bg-[#8C7B6B] text-white rounded-br-md"
                      : m.role === "admin"
                        ? "bg-[#1A1A1A] text-white rounded-bl-md"
                        : "bg-white border border-[#DDD7CF] text-[#1A1A1A] rounded-bl-md"
                  }`}
                >
                  {m.role === "admin" && (
                    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#C9BBA8] mb-1">
                      {m.admin_name || "Eternal Biolabs team"}
                    </p>
                  )}
                  {m.text || <span className="inline-flex gap-1"><Dot /><Dot d="150ms" /><Dot d="300ms" /></span>}
                </div>
              </div>
            ))}
            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 pt-1" data-testid="chat-starters">
                {STARTERS.map((s) => (
                  <button
                    key={s}
                    data-testid={`chat-starter-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                    onClick={() => send(s)}
                    disabled={sending}
                    className="text-xs px-3 py-1.5 rounded-none border border-[#8C7B6B] text-[#8C7B6B] bg-white hover:bg-[#8C7B6B] hover:text-white transition-colors disabled:opacity-50"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {!user && !emailSaved && (
            <div className="px-4 pt-3 bg-white border-t border-[#DDD7CF]">
              <input
                data-testid="chat-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email (optional — so we can follow up)"
                className="w-full text-xs border border-[#DDD7CF] rounded-none px-4 py-2 focus:outline-none focus:border-[#8C7B6B] bg-[#F7F5F2]"
              />
            </div>
          )}

          <div className={`flex items-center gap-2 px-4 py-3 bg-white ${user || emailSaved ? "border-t border-[#DDD7CF]" : ""}`}>
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
              placeholder="Type your question…"
              className="flex-1 min-w-0 text-sm border border-[#DDD7CF] rounded-none px-4 py-2.5 focus:outline-none focus:border-[#8C7B6B]"
            />
            <button
              data-testid="chat-send-button"
              onClick={() => send()}
              disabled={sending || !input.trim()}
              aria-label="Send message"
              className="w-10 h-10 shrink-0 rounded-full bg-[#8C7B6B] text-white flex items-center justify-center hover:bg-[#75665A] transition-colors disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[10px] text-[#6E675F] text-center pb-2 bg-white">
            Research-use questions only — no medical or dosing advice.
          </p>
        </div>
      )}

      <button
        data-testid="chat-widget-button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close support chat" : "Open support chat"}
        className="fixed bottom-6 right-4 sm:right-6 z-50 w-14 h-14 rounded-full bg-[#8C7B6B] text-white flex items-center justify-center shadow-xl hover:bg-[#75665A] hover:scale-105 transition-transform duration-200"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
    </>
  );
};

const Dot = ({ d = "0ms" }) => (
  <span className="w-1.5 h-1.5 rounded-full bg-[#8C7B6B] animate-bounce inline-block" style={{ animationDelay: d }} />
);
